import React, { useMemo, useState, memo, useCallback, useEffect } from 'react';
import {
  FileText,
  CheckCircle,
  Loader,
  Eye,
  WandSparkles,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useSelector, useDispatch } from 'react-redux';
import { StepActionButton, DiscreteActionsDropdown } from '../ApplicationStepComponents';
import {
  selectIllustration,
  selectSession,
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
  postIllustrationRequestRequest,
  selectSessionAdvisor,
} from '@client/model';
import { StepAlert } from '../ApplicationStepComponents';
import { UpdatedQuoteSection } from './UpdatedQuoteSection';
import { useIllustrationDownloadFlow } from '@client/hooks/useIllustrationDownloadFlow';
import { useIllustrationPolling } from '@client/hooks/useIllustrationPolling';
import { useIllustrationMetadata } from '@client/hooks/useIllustrationMetadata';
import { usePolicyApplicationStepActions } from '@client/hooks/usePolicyApplicationStepActions';
import { useIllustrationConsentFlow } from '@client/hooks/useIllustrationConsentFlow';
import { useIllustrationStepPersistence } from '@client/hooks/useIllustrationStepPersistence';
import { getQuoteId } from '@client/util/quoteUtils';
import { SessionType, buildIllustrationRequestPayload, FundingPolicyType } from '@shared';
import { ContactSupportButton } from '@client';
import { useApplyPolicyUpdatesToDatabase } from '@client/hooks/useApplyPolicyUpdatesToDatabase';

interface DownloadPolicyIllustrationApplicationStepProps {
  clientPolicyApplicationId: string;
  onContinue: () => void;
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
  clientPartner?: Client | null;
  step: PolicyApplicationStep;
  fundingSourceLabel: string;
  onStepSelect?: React.Dispatch<React.SetStateAction<string | null>>;
  client: Client;
  policyApplication: ClientPolicyApplication;
  needsIllustrationBeforeSigning?: boolean;
}

interface StepAction {
  type: 'primary' | 'secondary';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export const DownloadPolicyIllustrationApplicationStep: React.FC<DownloadPolicyIllustrationApplicationStepProps> =
  memo(
    ({
      clientPolicyApplicationId,
      onContinue,
      sourceQuote,
      clientPartner,
      step,
      fundingSourceLabel,
      client,
      policyApplication,
      needsIllustrationBeforeSigning,
    }) => {
      const dispatch = useDispatch();
      const illustration = useSelector(selectIllustration);
      const { sessionType } = useSelector(selectSession);
      const advisor = useSelector(selectSessionAdvisor);
      const [hasConfirmed, setHasConfirmed] = useState(false);
      const stepMetadata = step.metadata;
      const stepStatus = step.status;

      useIllustrationPolling();

      // Fetch consent status on mount
      useEffect(() => {
        dispatch(
          fetchApplicationConsentStatusByClientPolicyApplicationIdRequest({
            clientPolicyApplicationId,
            queryParams: { consentType: 'download' },
          }),
        );
      }, [clientPolicyApplicationId, dispatch]);

      const {
        hasIllustrationPdf,
        isCreatingIllustration,
        handleViewPdf,
        handleCreateIllustration,
      } = useIllustrationDownloadFlow(
        useMemo(
          () => ({
            quote: sourceQuote,
            selectedPremium: policyApplication.illustrationPremiumAmount,
            clientPartner,
            fundingSourceId: policyApplication.fundingSourceId,
            clientPolicyApplicationId,
          }),
          [
            sourceQuote,
            policyApplication.illustrationPremiumAmount,
            clientPartner,
            policyApplication.fundingSourceId,
            clientPolicyApplicationId,
          ],
        ),
      );

      const quoteId = useMemo(() => getQuoteId(sourceQuote), [sourceQuote]);

      const illustrationError = useMemo(() => {
        if (!illustration.erroredRequests || !quoteId) return null;
        return illustration.erroredRequests[quoteId];
      }, [illustration.erroredRequests, quoteId]);

      const hasIllustrationError = Boolean(
        !hasIllustrationPdf &&
        (stepStatus === 'error' || (illustrationError && illustrationError.errors.length > 0)),
      );

      // Custom hooks for complex logic
      const { buildIllustrationMetadata } = useIllustrationMetadata({
        sourceQuote,
        illustrationError,
        client,
        policyApplication,
      });

      const { handleSaveIllustrationError, handleSkipStep } = usePolicyApplicationStepActions({
        clientPolicyApplicationId,
        stepType: 'download-policy-illustration',
        hasIllustrationError,
        buildIllustrationMetadata,
        onContinue,
      });

      const { handleContinueWithIllustration, postConsentRecordIsLoading } =
        useIllustrationConsentFlow({
          clientPolicyApplicationId,
          sourceQuote,
          client,
          policyApplication,
          onContinue,
          buildIllustrationMetadata,
        });

      // Handle illustration state persistence to/from policy application step
      useIllustrationStepPersistence({
        clientPolicyApplicationId,
        stepMetadata,
        stepStatus,
        hasIllustrationPdf,
        hasIllustrationError,
        isCreatingIllustration,
        buildIllustrationMetadata,
        handleSaveIllustrationError,
      });

      // Hook for applying policy updates to database
      const { applyPolicyUpdatesToDatabase } = useApplyPolicyUpdatesToDatabase({
        client,
        policyApplication,
        clientPolicyApplicationId,
      });

      const handleDownloadOrViewPdf = useCallback(() => {
        if (hasIllustrationPdf) {
          handleViewPdf({ stopPropagation: () => {} } as React.MouseEvent);
        } else {
          handleCreateIllustration();
        }
      }, [hasIllustrationPdf, handleViewPdf, handleCreateIllustration]);

      const getButtonText = useCallback(() => {
        if (isCreatingIllustration) return 'Creating...';
        if (hasIllustrationError) {
          // For CareScout, don't allow skipping on error via button
          if (policyApplication.fundingPolicyType === FundingPolicyType.careScoutCareAssurance) {
            return 'Retry Illustration';
          }
          return 'Continue Without Illustration';
        }
        return 'Download PDF';
      }, [isCreatingIllustration, hasIllustrationError, policyApplication.fundingPolicyType]);

      const handleApplyPolicyUpdates = useCallback(() => {
        const result = applyPolicyUpdatesToDatabase();
        if (!result.success) {
          console.warn('Failed to apply policy updates:', result.message);
        }
      }, [applyPolicyUpdatesToDatabase]);

      const handleTestErrorFlow = useCallback(() => {
        try {
          const payload = buildIllustrationRequestPayload({
            quote: sourceQuote,
            client,
            clientPartner,
            advisor,
            selectedPremium: policyApplication.illustrationPremiumAmount,
            fundingSourceId: policyApplication.fundingSourceId,
            clientPolicyApplicationId,
          });

          dispatch(postIllustrationRequestRequest({ ...payload, simulateError: true }));
        } catch (error) {
          console.error('Failed to build illustration request payload:', error);
        }
      }, [
        sourceQuote,
        client,
        clientPartner,
        advisor,
        policyApplication.illustrationPremiumAmount,
        policyApplication.fundingSourceId,
        clientPolicyApplicationId,
        dispatch,
      ]);

      const actions = useMemo((): StepAction[] => {
        const actionsList: StepAction[] = [];

        if (stepStatus === 'completed') {
          return actionsList;
        }

        if (!hasIllustrationPdf) {
          // Determine if this is a CareScout policy
          const isCareScoutPolicy =
            policyApplication.fundingPolicyType === FundingPolicyType.careScoutCareAssurance;

          // For CareScout policies with errors: force retry via button
          // For other policies with errors: allow skip via button
          // For no errors: attempt download/view
          const shouldForceRetry = hasIllustrationError && isCareScoutPolicy;
          const shouldAllowSkip = hasIllustrationError && !isCareScoutPolicy;

          const handleClick = shouldForceRetry
            ? handleDownloadOrViewPdf
            : shouldAllowSkip
              ? handleSkipStep
              : handleDownloadOrViewPdf;

          actionsList.push({
            type: 'primary',
            label: getButtonText(),
            onClick: handleClick,
            disabled: isCreatingIllustration || postConsentRecordIsLoading,
            isLoading: isCreatingIllustration,
          });
        }

        if (hasIllustrationPdf && hasConfirmed) {
          actionsList.push({
            type: 'primary',
            label: postConsentRecordIsLoading ? 'Processing...' : 'Continue to Application',
            onClick: () => {
              handleApplyPolicyUpdates();
              handleContinueWithIllustration();
            },
            disabled: postConsentRecordIsLoading,
            isLoading: postConsentRecordIsLoading,
          });
        }
        return actionsList;
      }, [
        hasIllustrationPdf,
        hasConfirmed,
        stepStatus,
        getButtonText,
        handleDownloadOrViewPdf,
        handleContinueWithIllustration,
        isCreatingIllustration,
        postConsentRecordIsLoading,
        hasIllustrationError,
        handleSkipStep,
      ]);

      const showFooter = useMemo(() => {
        return (
          actions.length > 0 ||
          hasIllustrationError ||
          (stepStatus !== 'skipped' &&
            stepStatus !== 'completed' &&
            sessionType === SessionType.wizard)
        );
      }, [actions.length, hasIllustrationError, stepStatus, sessionType]);

      return (
        <div className="p-2">
          {needsIllustrationBeforeSigning ? (
            // Unique view for exception case
            <>
              <div className="mb-4">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">
                  Illustration Required Before Submitting
                </h3>
                <p className="text-gray-700">
                  Before you can submit your application, we need to generate your policy
                  illustration.
                </p>
              </div>

              {!isCreatingIllustration && (
                <div className="mb-8">
                  <StepAlert
                    title="Potential Carrier System Issue"
                    description="Your policy illustration is needed to finalize the details before submitting your application. Our team is actively working on generating this for you and will reach out once it's ready. You can also try generating it again below."
                    variant="warning"
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col items-end gap-4 pt-4">
                <div className="flex flex-wrap justify-end gap-4">
                  <ContactSupportButton
                    subject="Policy Illustration Required Before Submitting"
                    fallbackEmail="hello@joinwaterlily.com"
                  >
                    Contact Support
                  </ContactSupportButton>
                  <StepActionButton
                    onClick={handleDownloadOrViewPdf}
                    disabled={isCreatingIllustration}
                    variant="default"
                    icon={
                      isCreatingIllustration ? (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )
                    }
                  >
                    Retry Illustration
                  </StepActionButton>
                </div>
              </div>
            </>
          ) : (
            // Normal view
            <>
              <div className="mb-4">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">
                  Download Policy Illustration
                </h3>
                <p className="text-gray-700">
                  {stepStatus === 'completed'
                    ? `Downloaded and reviewed the policy illustration for your ${fundingSourceLabel} application.`
                    : `Download and review the policy illustration to continue with your ${fundingSourceLabel} application. This document contains final rates and coverage details.`}
                </p>
              </div>

              <div className="mb-8">
                {/* Success message for completed step */}
                {stepStatus === 'completed' && (
                  <div className="mb-4 text-sm text-green-600">
                    ✓ Policy illustration was previously downloaded. You can view or re-download it
                    below.
                  </div>
                )}

                {/* Error Alert */}
                {hasIllustrationError && (
                  <StepAlert
                    title="Your illustration couldn't be created"
                    description={`This step depends on an external system that could be experiencing downtime. `}
                    variant="warning"
                    actions={[
                      {
                        label: isCreatingIllustration ? 'Retrying...' : 'Retry Illustration',
                        onClick: () => {
                          handleDownloadOrViewPdf();
                        },
                        variant: 'outline',
                        disabled: isCreatingIllustration,
                        isLoading: isCreatingIllustration,
                      },
                    ]}
                  />
                )}

                {/* Download illustration section */}
                {!hasIllustrationError && (
                  <div className="mb-4 rounded-lg border border-gray-200 bg-white p-5 sm:p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      {/* Left side: File info and metadata */}
                      <div className="flex items-start gap-3">
                        <div className="bg-primary-50 mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg">
                          <FileText className="text-primary h-5 w-5" />
                        </div>
                        <div className="min-w-0 space-y-2">
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">
                              Policy Illustration
                            </h3>
                            <p className="mt-0.5 text-sm text-gray-700">
                              Your personalized premium rates and coverage details
                            </p>
                          </div>

                          {/* Metadata row */}
                          <div className="flex flex-wrap items-center gap-3">
                            {hasIllustrationPdf &&
                              stepMetadata?.illustrationRequest?.createdDateTime && (
                                <p className="text-xs text-gray-700">
                                  Created{' '}
                                  {new Date(
                                    stepMetadata.illustrationRequest.createdDateTime,
                                  ).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </p>
                              )}

                            {/* Status chip */}
                            {hasIllustrationPdf && !isCreatingIllustration && (
                              <div className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                                <CheckCircle className="h-3.5 w-3.5" />
                                <span>Ready</span>
                              </div>
                            )}

                            {/* Generating status */}
                            {isCreatingIllustration && (
                              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                                <Loader className="h-3.5 w-3.5 animate-spin" />
                                <span>Generating...</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right side: Action buttons */}
                      <div className="flex flex-shrink-0 flex-col items-end gap-3 sm:ml-4">
                        {isCreatingIllustration && (
                          <p className="text-xs text-gray-700">This may take a few minutes</p>
                        )}

                        {hasIllustrationPdf && (
                          <>
                            <Button
                              onClick={handleViewPdf}
                              size="sm"
                              className="h-10 px-5 text-sm font-medium"
                            >
                              <Eye className="mr-1.5 h-4 w-4" />
                              View Illustration
                            </Button>
                            <Button
                              onClick={handleCreateIllustration}
                              variant="ghost"
                              size="sm"
                              className="h-auto px-2 py-1 text-xs text-gray-700 hover:text-gray-700"
                              disabled={isCreatingIllustration}
                            >
                              Regenerate Illustration
                            </Button>
                          </>
                        )}

                        {!hasIllustrationPdf &&
                          stepStatus === 'completed' &&
                          !isCreatingIllustration && (
                            <Button
                              onClick={handleDownloadOrViewPdf}
                              size="sm"
                              className="h-10 px-5 text-sm font-medium"
                            >
                              <Eye className="mr-1.5 h-4 w-4" />
                              Download PDF
                            </Button>
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {hasIllustrationPdf && stepStatus !== 'completed' && (
                  <UpdatedQuoteSection
                    clientPolicyApplicationId={clientPolicyApplicationId}
                    sourceQuote={sourceQuote}
                  />
                )}

                {hasIllustrationPdf && stepStatus !== 'completed' && (
                  <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-900">
                      <span className="font-semibold">Important:</span> This is still an estimate
                      before formal underwriting review. Final pricing depends on the carrier's
                      health class assignment (e.g., Preferred or Standard).
                    </p>
                  </div>
                )}

                {hasIllustrationPdf && stepStatus !== 'completed' && (
                  <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="confirm-documents"
                        checked={hasConfirmed}
                        onCheckedChange={checked => setHasConfirmed(!!checked)}
                        className="mt-1"
                      />
                      <label
                        htmlFor="confirm-documents"
                        className="cursor-pointer text-sm leading-relaxed text-gray-700"
                      >
                        I understand this is an updated estimate and the final price depends on
                        underwriting.
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              {showFooter && (
                <div className="flex flex-col items-end gap-4 pt-4">
                  <div className="flex flex-wrap justify-end gap-4">
                    {stepStatus !== 'skipped' &&
                      stepStatus !== 'completed' &&
                      sessionType === SessionType.wizard && (
                        <DiscreteActionsDropdown
                          actions={[
                            {
                              id: 'skip',
                              label: 'Skip Step',
                              icon: <WandSparkles className="h-4 w-4" />,
                              onClick: handleSkipStep,
                              disabled: postConsentRecordIsLoading,
                              isLoading: postConsentRecordIsLoading,
                              loadingText: 'Processing...',
                            },
                            {
                              id: 'test-error-flow',
                              label: 'Test Error Flow',
                              icon: <AlertCircle className="h-4 w-4" />,
                              onClick: handleTestErrorFlow,
                              disabled: isCreatingIllustration || postConsentRecordIsLoading,
                            },
                          ]}
                        />
                      )}
                    {hasIllustrationError && (
                      <ContactSupportButton
                        subject="Policy Illustration Error"
                        fallbackEmail="hello@joinwaterlily.com"
                      >
                        Contact Support
                      </ContactSupportButton>
                    )}

                    {/* Then render primary/secondary buttons */}
                    {actions.map((action, index) => (
                      <StepActionButton
                        key={`action-${index}`}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        variant={action.type === 'secondary' ? 'outline' : 'default'}
                        icon={<></>}
                      >
                        {action.label}
                      </StepActionButton>
                    ))}
                  </div>
                  {hasIllustrationError && (
                    <p className="text-sm  italic text-gray-700">
                      We'll keep retrying this in the background.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    },
  );
